import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";
import { X as CloseIcon } from "../icons.phosphor.js";
import { releaseBodyScrollLockIfNoModalOpen } from "../internal/body-scroll-lock.js";
import { cx } from "../internal/cx.js";

export type DialogSize = "sm" | "md" | "lg" | "xl" | "full";

/* ─── Primitives (Radix re-exports) ─── */

export function Dialog({
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  return (
    <DialogPrimitive.Root
      {...props}
      onOpenChange={(open) => {
        onOpenChange?.(open);
        if (!open) {
          requestAnimationFrame(() => releaseBodyScrollLockIfNoModalOpen());
        }
      }}
    />
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

/**
 * Dimming layer behind the content. On mobile the overlay sits below a
 * bottom-sheet content panel; on desktop it sits behind a centered card.
 * Both states share the same opacity + fade animation.
 */
export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cx("cw-v2-dialog__overlay", className)}
      {...props}
    />
  );
});
DialogOverlay.displayName = "DialogOverlay";

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Controls the desktop max-width. Default 'md'. Mobile ignores this — it always fills the viewport width. */
  size?: DialogSize;
  /** Hide the default close (X) button in the top-right corner. */
  hideCloseButton?: boolean;
  /** Override the accessible label for the close button. */
  closeLabel?: string;
}

/**
 * Dialog content surface.
 *
 * Below 640px the dialog renders as a bottom sheet (full-width, square-cornered,
 * slides up from the bottom). At 640px and above it renders as a centered
 * modal card. The same component handles both — there is no separate mobile
 * variant — so consumers don't have to think about it.
 *
 * Scroll behavior:
 *   - Desktop: header and footer are pinned to the top and bottom of the
 *     dialog; the body has its own internal scrollbar.
 *   - Mobile:  header is pinned at the top of the sheet; body and footer
 *     share a single scroll area, so the footer flows at the END of the
 *     content (no fixed action bar — keeps action buttons out from under
 *     iOS Safari's dynamic URL bar).
 *
 * The internal `__inner` wrapper exists so the close (X) button can stay
 * pinned to the dialog's top-right corner regardless of which scroll mode
 * the body is using.
 */
export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(function DialogContent(
  {
    children,
    className,
    closeLabel = "Close dialog",
    hideCloseButton = false,
    onAnimationEnd,
    onCloseAutoFocus,
    size = "md",
    ...props
  },
  ref,
) {
  const scheduleScrollLockRelease = React.useCallback(() => {
    requestAnimationFrame(() => {
      releaseBodyScrollLockIfNoModalOpen();
    });
  }, []);

  React.useEffect(() => () => scheduleScrollLockRelease(), [scheduleScrollLockRelease]);

  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cx(
          "cw-v2-theme",
          "cw-v2-dialog__content",
          `cw-v2-dialog__content--${size}`,
          className,
        )}
        onAnimationEnd={(event) => {
          onAnimationEnd?.(event);
          if (event.currentTarget !== event.target) return;
          if (event.currentTarget.getAttribute("data-state") !== "closed") return;
          scheduleScrollLockRelease();
        }}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          scheduleScrollLockRelease();
        }}
        {...props}
      >
        {hideCloseButton ? null : (
          <DialogPrimitive.Close aria-label={closeLabel} className="cw-v2-dialog__close">
            <CloseIcon className="cw-v2-dialog__close-icon" weight="bold" aria-hidden />
          </DialogPrimitive.Close>
        )}
        <div className="cw-v2-dialog__inner">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});
DialogContent.displayName = "DialogContent";

/* ─── Layout subcomponents ─── */

/**
 * Top region of the dialog. Contains title + description with the close
 * button rendered alongside by DialogContent. Use this even when there's
 * no description; it owns the spacing rules around the title.
 */
export const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function DialogHeader({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-dialog__header", className)} {...props} />;
  },
);
DialogHeader.displayName = "DialogHeader";

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title ref={ref} className={cx("cw-v2-dialog__title", className)} {...props} />
  );
});
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cx("cw-v2-dialog__description", className)}
      {...props}
    />
  );
});
DialogDescription.displayName = "DialogDescription";

/**
 * Middle region of the dialog. Has natural height; the dialog itself is the
 * scroll container, so long bodies make the whole sheet scroll rather than
 * introducing a nested scrollbar.
 */
export const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function DialogBody({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-dialog__body", className)} {...props} />;
  },
);
DialogBody.displayName = "DialogBody";

/**
 * Action row that flows immediately after the body — NOT pinned to the
 * bottom of the dialog. Right-aligns on desktop; stacks full-width on mobile
 * (with the primary action on the bottom row, closest to the thumb).
 */
export const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function DialogFooter({ className, ...props }, ref) {
    return <div ref={ref} className={cx("cw-v2-dialog__footer", className)} {...props} />;
  },
);
DialogFooter.displayName = "DialogFooter";
